import { useCallback, useEffect, useRef, useState } from 'react';
import {
  buildChunkHeader,
  newTransferId,
  sendFileControl,
  FILE_CHUNK_HEADER_LEN,
  FILE_CHUNK_PAYLOAD_LIMIT,
  type FileMessage,
} from './protocol';

const HIGH_WATERMARK = 1 * 1024 * 1024; // pause sending above 1 MiB pending
const LOW_WATERMARK = 64 * 1024; // resume at 64 KiB pending

export type TransferState =
  | { kind: 'pending' }
  | { kind: 'sending'; sent: number; total: number }
  | { kind: 'finalising' } // sent FileComplete, waiting for FileDone
  | { kind: 'done' }
  | { kind: 'failed'; error: string };

export interface Transfer {
  id: string;
  name: string;
  size: number;
  state: TransferState;
  startedAt: number;
  /** Bytes-per-second moving estimate, refreshed on every progress tick. */
  bps?: number;
}

interface UseFileUploadOpts {
  filesChannel: RTCDataChannel | null;
  enabled: boolean;
}

interface UseFileUploadApi {
  transfers: Transfer[];
  /** Start uploading the given `File`. Returns the transfer id. */
  upload: (file: File) => string;
  cancel: (id: string) => void;
  /** Drop already-finished entries from the list (used by UI cleanup). */
  clear: () => void;
}

/**
 * Browser→host file upload over the `files` DataChannel. Each transfer runs as an async task that
 * reads the `File` in slices, packs every slice into a 24-byte-prefixed binary frame, and writes
 * via `RTCDataChannel.send`. Flow control:
 *
 *   - We watch `bufferedAmount`. Above `HIGH_WATERMARK` we await the next `onbufferedamountlow`
 *     event (set via `bufferedAmountLowThreshold = LOW_WATERMARK`). This caps memory pressure on
 *     the SCTP send queue and prevents Chrome from killing the channel with a "queue full" error.
 *   - Reads are done via `Blob.slice(...).arrayBuffer()` which streams from disk on Chrome 60+,
 *     so even multi-GiB files don't load fully into RAM.
 */
export function useFileUpload({ filesChannel, enabled }: UseFileUploadOpts): UseFileUploadApi {
  const channelRef = useRef(filesChannel);
  const enabledRef = useRef(enabled);
  const transfersRef = useRef<Map<string, InternalTransfer>>(new Map());
  const [, forceRender] = useState(0);
  const rerender = useCallback(() => forceRender((n) => n + 1), []);

  useEffect(() => {
    channelRef.current = filesChannel;
    if (filesChannel) {
      filesChannel.binaryType = 'arraybuffer';
      filesChannel.bufferedAmountLowThreshold = LOW_WATERMARK;
    }
  }, [filesChannel]);
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  // Listen for inbound JSON file-control messages (FileAccept / FileProgress / FileDone / FileError).
  useEffect(() => {
    if (!filesChannel) return;
    const onMessage = (ev: MessageEvent) => {
      // Binary frames are inbound chunks — only relevant once we add host→browser direction.
      if (typeof ev.data !== 'string') return;
      let msg: FileMessage;
      try {
        msg = JSON.parse(ev.data) as FileMessage;
      } catch {
        return;
      }
      const t = transfersRef.current.get(msg.id);
      if (!t) return;
      switch (msg.type) {
        case 'file_accept':
          t.acceptedResolve?.();
          t.acceptedResolve = undefined;
          break;
        case 'file_progress':
          if (t.public.state.kind === 'sending') {
            t.public.state = { kind: 'sending', sent: msg.bytes_received, total: msg.bytes_received > t.public.size ? msg.bytes_received : t.public.size };
            rerender();
          }
          break;
        case 'file_done':
          t.public.state = { kind: 'done' };
          rerender();
          break;
        case 'file_error':
          t.public.state = { kind: 'failed', error: msg.error };
          t.cancelled = true;
          rerender();
          break;
        default:
          break;
      }
    };
    filesChannel.addEventListener('message', onMessage);
    return () => filesChannel.removeEventListener('message', onMessage);
  }, [filesChannel, rerender]);

  const upload = useCallback(
    (file: File): string => {
      const id = newTransferId();
      const t: InternalTransfer = {
        public: {
          id,
          name: file.name,
          size: file.size,
          state: { kind: 'pending' },
          startedAt: performance.now(),
        },
        cancelled: false,
      };
      transfersRef.current.set(id, t);
      rerender();
      // Fire off the async upload task; errors arrive via state.kind === 'failed'.
      runUpload(t, file, channelRef, enabledRef, rerender).catch((e) => {
        t.public.state = { kind: 'failed', error: String((e as Error).message ?? e) };
        rerender();
      });
      return id;
    },
    [rerender],
  );

  const cancel = useCallback((id: string) => {
    const t = transfersRef.current.get(id);
    if (!t) return;
    t.cancelled = true;
    sendFileControl(channelRef.current, { type: 'file_cancel', id });
    if (t.public.state.kind === 'sending' || t.public.state.kind === 'pending') {
      t.public.state = { kind: 'failed', error: 'cancelled' };
    }
    rerender();
  }, [rerender]);

  const clear = useCallback(() => {
    for (const [id, t] of Array.from(transfersRef.current.entries())) {
      if (t.public.state.kind === 'done' || t.public.state.kind === 'failed') {
        transfersRef.current.delete(id);
      }
    }
    rerender();
  }, [rerender]);

  const transfers = Array.from(transfersRef.current.values()).map((t) => t.public);
  return { transfers, upload, cancel, clear };
}

interface InternalTransfer {
  public: Transfer;
  cancelled: boolean;
  acceptedResolve?: () => void;
}

async function runUpload(
  t: InternalTransfer,
  file: File,
  channelRef: React.MutableRefObject<RTCDataChannel | null>,
  enabledRef: React.MutableRefObject<boolean>,
  rerender: () => void,
): Promise<void> {
  if (!channelRef.current || !enabledRef.current) {
    throw new Error('files DataChannel not ready');
  }

  // Wait for FileAccept before sending bytes — host might reject (size cap, IO failure, etc).
  const acceptedPromise = new Promise<void>((resolve) => {
    t.acceptedResolve = resolve;
  });
  const sha256 = await sha256Hex(file).catch(() => undefined);
  sendFileControl(channelRef.current, {
    type: 'file_offer',
    id: t.public.id,
    name: file.name,
    size: file.size,
    sha256,
  });
  const acceptTimeout = new Promise<never>((_, rej) =>
    setTimeout(() => rej(new Error('host did not respond to file_offer within 10s')), 10_000),
  );
  await Promise.race([acceptedPromise, acceptTimeout]);
  if (t.cancelled) return;

  // Start streaming.
  t.public.state = { kind: 'sending', sent: 0, total: file.size };
  rerender();

  let offset = 0;
  const chunkSize = FILE_CHUNK_PAYLOAD_LIMIT;
  while (offset < file.size) {
    if (t.cancelled || !channelRef.current || channelRef.current.readyState !== 'open') return;
    // Backpressure: wait until the DC has drained.
    while (channelRef.current.bufferedAmount > HIGH_WATERMARK) {
      await waitForBufferedAmountLow(channelRef.current);
      if (t.cancelled) return;
    }
    const end = Math.min(offset + chunkSize, file.size);
    const slice = file.slice(offset, end);
    const buf = await slice.arrayBuffer();
    const frame = new Uint8Array(FILE_CHUNK_HEADER_LEN + buf.byteLength);
    frame.set(new Uint8Array(buildChunkHeader(t.public.id, offset)), 0);
    frame.set(new Uint8Array(buf), FILE_CHUNK_HEADER_LEN);
    channelRef.current.send(frame.buffer);
    offset = end;
    t.public.state = { kind: 'sending', sent: offset, total: file.size };
    // Cheap throughput estimate: bytes ÷ elapsed seconds.
    const elapsedMs = Math.max(1, performance.now() - t.public.startedAt);
    t.public.bps = (offset * 1000) / elapsedMs;
    rerender();
  }

  // All bytes sent. Tell host to finalise.
  t.public.state = { kind: 'finalising' };
  rerender();
  sendFileControl(channelRef.current, { type: 'file_complete', id: t.public.id });
  // FileDone / FileError arrives via the message listener (state → done | failed).
}

function waitForBufferedAmountLow(ch: RTCDataChannel): Promise<void> {
  return new Promise((resolve) => {
    const onLow = () => {
      ch.removeEventListener('bufferedamountlow', onLow);
      resolve();
    };
    ch.addEventListener('bufferedamountlow', onLow);
  });
}

/**
 * SHA-256 over the entire file via SubtleCrypto, streaming via slicing to avoid full-file
 * memory pressure. For very large files this still copies — Chrome's SubtleCrypto doesn't expose
 * a streaming digest. Falls back to skipping the hash for files > 200 MiB.
 */
async function sha256Hex(file: File): Promise<string | undefined> {
  if (file.size > 200 * 1024 * 1024) return undefined; // skip — host accepts files without checksum
  try {
    const buf = await file.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return undefined;
  }
}
